if (!customElements.get('footer-banner')) {
  customElements.define(
    'footer-banner',
    class FooterBanner extends HTMLElement {
      connectedCallback() {
        this.textEl = this.querySelector('[data-scroll-text]');
        if (!this.textEl) return;

        this.words = [];
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.smoothing = parseFloat(this.dataset.scrollSmoothness || '0.1');
        this.driftStrength = parseFloat(this.dataset.scrollDrift || '3');
        this.rafId = null;

        this.splitWords();

        this.current = {
          x: 0,
          y: 8,
          scale: 0.96,
          opacity: 0,
          progress: 0,
        };
        this.target = { ...this.current };

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onScroll.bind(this);

        if (this.reducedMotion) {
          this.settleStatic();
          return;
        }

        this.updateTargetFromScroll();
        this.current = { ...this.target };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });

        this.classList.add('is-scroll-ready');
        this.applyStyles();
        this.startAnimationLoop();
      }

      disconnectedCallback() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }

      splitWords() {
        const raw = this.textEl.textContent.trim();
        if (!raw) return;

        const parts = raw.split(/(\s+)/);
        this.textEl.textContent = '';
        this.textEl.setAttribute('aria-label', raw);

        parts.forEach((part) => {
          if (/^\s+$/.test(part)) {
            this.textEl.appendChild(document.createTextNode(part));
            return;
          }

          const word = document.createElement('span');
          word.className = 'footer__banner-word';
          word.setAttribute('aria-hidden', 'true');
          word.textContent = part;
          this.textEl.appendChild(word);
          this.words.push(word);
        });
      }

      settleStatic() {
        this.classList.add('is-scroll-settled');
        this.textEl.style.setProperty('--fbt-opacity', '1');
        this.textEl.style.setProperty('--fbt-y', '0');
        this.textEl.style.setProperty('--fbt-x', '0');
        this.textEl.style.setProperty('--fbt-scale', '1');

        this.words.forEach((word) => {
          word.style.setProperty('--fbt-word-y', '0');
          word.style.setProperty('--fbt-word-opacity', '1');
        });
      }

      onScroll() {
        this.updateTargetFromScroll();
      }

      easeOutCubic(value) {
        return 1 - Math.pow(1 - value, 3);
      }

      clamp(value, min, max) {
        return Math.min(max, Math.max(min, value));
      }

      lerp(current, target, factor) {
        return current + (target - current) * factor;
      }

      updateTargetFromScroll() {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const sectionHeight = Math.max(rect.height, viewportHeight * 0.35);
        const enterStart = viewportHeight;
        const enterEnd = viewportHeight - sectionHeight * 0.35;
        const exitStart = viewportHeight * 0.55;
        const exitEnd = -sectionHeight * 0.15;

        const enterProgress = this.clamp(
          (enterStart - rect.top) / Math.max(enterStart - enterEnd, 1),
          0,
          1
        );
        const exitProgress = this.clamp(
          (exitStart - rect.top) / Math.max(exitStart - exitEnd, 1),
          0,
          1
        );

        const reveal = this.easeOutCubic(enterProgress);
        const travel = this.easeOutCubic(exitProgress);

        this.target.progress = travel;
        this.target.opacity = reveal;
        this.target.y = 8 * (1 - reveal) + -4 * travel;
        this.target.scale = 0.96 + 0.04 * reveal - 0.02 * travel;
        this.target.x = (travel - 0.5) * 2 * this.driftStrength;
      }

      applyStyles() {
        this.textEl.style.setProperty('--fbt-opacity', this.current.opacity.toFixed(4));
        this.textEl.style.setProperty('--fbt-y', `${this.current.y.toFixed(3)}vh`);
        this.textEl.style.setProperty('--fbt-x', `${this.current.x.toFixed(3)}vw`);
        this.textEl.style.setProperty('--fbt-scale', this.current.scale.toFixed(4));

        const wordCount = this.words.length;
        if (!wordCount) return;

        this.words.forEach((word, index) => {
          const stagger = index / Math.max(wordCount - 1, 1);
          const wordReveal = this.clamp((this.current.opacity - stagger * 0.35) / 0.65, 0, 1);
          const easedWord = this.easeOutCubic(wordReveal);

          word.style.setProperty('--fbt-word-y', `${(1 - easedWord) * 1.4}em`);
          word.style.setProperty('--fbt-word-opacity', easedWord.toFixed(4));
        });

        if (this.current.opacity > 0.98 && Math.abs(this.current.y) < 0.05) {
          this.classList.add('is-scroll-settled');
        } else {
          this.classList.remove('is-scroll-settled');
        }
      }

      startAnimationLoop() {
        const animate = () => {
          this.current.opacity = this.lerp(this.current.opacity, this.target.opacity, this.smoothing);
          this.current.y = this.lerp(this.current.y, this.target.y, this.smoothing);
          this.current.x = this.lerp(this.current.x, this.target.x, this.smoothing);
          this.current.scale = this.lerp(this.current.scale, this.target.scale, this.smoothing);
          this.current.progress = this.lerp(this.current.progress, this.target.progress, this.smoothing);

          this.applyStyles();
          this.rafId = requestAnimationFrame(animate);
        };

        this.rafId = requestAnimationFrame(animate);
      }
    }
  );
}
